import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import LoadingSpinner from "../common/LoadingSpinner";

const AttendanceSheet = ({ course, date, canEdit = true }) => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAttendance = async () => {
      if (!course?._id || !date) return;

      setLoading(true);
      try {
        const response = await api.get("/attendance", {
          params: {
            courseId: course._id,
            date: new Date(date).toISOString().split("T")[0],
          },
        });

        const attendanceData = response.data.data?.attendance || [];
        setAttendance(attendanceData);
      } catch (error) {
        toast.error("Failed to load attendance data");
        console.error("Attendance fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [course, date]);

  const handleStatusChange = (studentId, status) => {
    setAttendance((prev) =>
      prev.map((item) =>
        item.student._id === studentId ? { ...item, status } : item
      )
    );
  };

  const handleNotesChange = (studentId, notes) => {
    setAttendance((prev) =>
      prev.map((item) =>
        item.student._id === studentId ? { ...item, notes } : item
      )
    );
  };

  const handleSave = async () => {
    if (!attendance.length) {
      toast.warning("No attendance data to save");
      return;
    }

    setSaving(true);
    try {
      await api.post(
        "/attendance",
        {
          courseId: course._id,
          date: new Date(date).toISOString().split("T")[0],
          attendanceData: attendance.map((item) => ({
            studentId: item.student._id,
            present: item.status === "present",
            notes: item.notes,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Attendance saved successfully!");
    } catch (error) {
      console.error("Save error:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Failed to save attendance");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading attendance..." />;

  return (
    <div className="attendance-sheet p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">
        {course?.name} - {new Date(date).toLocaleDateString()}
      </h2>

      {attendance.length === 0 ? (
        <p className="text-center text-gray-500 py-4">No attendance records found</p>
      ) : (
        <div className="overflow-x-auto shadow-lg rounded-lg bg-white">
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-3 text-sm font-medium text-gray-600">Student</th>
                <th className="p-3 text-sm font-medium text-gray-600 text-center">Status</th>
                <th className="p-3 text-sm font-medium text-gray-600">Notes</th>
              </tr>
            </thead>
            <tbody>
              {attendance.map((record) => (
                <tr key={record.student._id} className="border-b">
                  <td className="p-3">{record.student.firstName} {record.student.lastName}</td>
                  <td className="p-3 text-center">
                    <select
                      value={record.status}
                      onChange={(e) =>
                        handleStatusChange(record.student._id, e.target.value)
                      }
                      disabled={!canEdit}
                      className="border rounded p-2 w-24 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    >
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                    </select>
                  </td>
                  <td className="p-3">
                    <input
                      type="text"
                      value={record.notes || ""}
                      onChange={(e) =>
                        handleNotesChange(record.student._id, e.target.value)
                      }
                      disabled={!canEdit}
                      className="border rounded p-2 w-full text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition duration-300"
          >
            {saving ? "Saving..." : "Save Attendance"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AttendanceSheet;
